import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Widget } from '@src/shared/ui/components/Widget';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Indicator } from '@uhg-abyss/web/ui/Indicator';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { isUndefined, orderBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { Table } from './components/Table';

/**
 * Widget: Attribute
 *
 * Displays reported information regarding the risk record's affiliated DOB or SSN.
 *
 * @param options
 * @param riskRecord
 * @returns {Element}
 * @constructor
 */
export function Attribute({ options = {}, riskRecord = {} }) {
  let attributeType = null;

  if (options?.accessor.includes('ssn')) {
    attributeType = 'ssn';
  }

  if (options?.accessor.includes('dob')) {
    attributeType = 'dob';
  }

  /**
   * check if the EID has attribute
   */
  const hasAttribute = useMemo(() => {
    return riskRecord?.ireRiskRecord?.remediationFindings?.find((attribute) => {
      return attribute.attributeType === attributeType;
    });
  }, [riskRecord?.ireRiskRecord?.remediationFindings, attributeType]);

  const ireRiskRecord = riskRecord?.ireRiskRecord;

  const [trustedValue, setTrustedValue] = useState('');
  const [attributeData, setAttributeData] = useState({});

  /**
   * store the attribute values and their respective sources and corresponding record IDs.
   */
  useEffect(() => {
    if (!isUndefined(ireRiskRecord?.remediationFindings) && hasAttribute) {
      /**
       * find the attribute
       */
      const theAttribute = ireRiskRecord?.remediationFindings.find((attribute) => {
        return attribute.attributeType === attributeType;
      });

      const theTrustedValue = theAttribute?.trustedValue;

      const values = {
        [theTrustedValue]: [],
      };

      /**
       * store trusted sources and their respective record IDs with the trusted value
       */
      ireRiskRecord?.trustedRecordSources?.forEach((record) => {
        const trustedRecord = {
          id: record?.recordId,
          source: record?.source,
        };

        if (!values[theTrustedValue].includes(trustedRecord)) {
          values[theTrustedValue].push(trustedRecord);
        }
      });

      /**
       * store untrusted values
       */
      theAttribute?.untrustedRecordSources?.forEach((record) => {
        if (isUndefined(values[record?.value])) {
          values[record?.value] = [];
        }
      });

      /**
       * store untrusted sources and their respective record IDs with the untrusted values
       */
      theAttribute?.untrustedRecordSources?.forEach((record) => {
        const untrustedRecord = {
          id: record?.recordId,
          source: record?.source,
        };

        if (!values[record?.value].includes(untrustedRecord)) {
          values[record?.value].push(untrustedRecord);
        }
      });

      if (theTrustedValue !== trustedValue) {
        setTrustedValue(theTrustedValue);
      }

      if (values !== attributeData) {
        setAttributeData(values);
      }
    }
  }, [hasAttribute, ireRiskRecord]);

  /**
   * accordionItems
   *
   * prepare the accordion items for the attribute.
   *
   * @type {*[]}
   */
  const accordionItems = useMemo(() => {
    return orderBy(
      Object.keys(attributeData).map((attributeValue) => {
        return {
          sourceCount: Object.keys(attributeData[attributeValue])?.length,
          sources: attributeData[attributeValue],
          type: String(attributeValue) === String(trustedValue) ? 'trusted' : 'untrusted',
          value: attributeValue,
        };
      }),
      ['type'],
      ['asc']
    );
  }, [attributeData]);

  return (
    <ErrorHandler location="src/widgets/RiskRecords/Attribute/Attribute.jsx">
      <Widget
        collapsed={false}
        collapsible
        icon={options?.icon}
        key={options?.title}
        title={`${options?.title}: ${String(attributeType).toUpperCase()}`}
      >
        {!hasAttribute ? (
          <Alert title={`There is no ${attributeType} data for this risk record.`} variant="info" />
        ) : (
          <Accordion isCollapsible type="multiple">
            {accordionItems?.map((item) => {
              const { sourceCount, sources, type, value } = item;

              return (
                <Accordion.Item key={`${item?.value}`} value={item?.value}>
                  <Accordion.Trigger>
                    <Accordion.Header>
                      <Layout.Group>
                        <div>
                          {type === 'trusted' ? (
                            <IconSymbol color="var(--abyss-colors-success1)" icon="check_circle" variant="outlined" />
                          ) : (
                            <IconSymbol color="var(--abyss-colors-error1)" icon="error" variant="outlined" />
                          )}
                        </div>
                        <div>
                          {sourceCount > 1 ? (
                            <div style={{ width: 'auto' }}>
                              <Indicator
                                color="var(--abyss-colors-info1)"
                                label={sourceCount}
                                showZero={false}
                                withBorder
                              >
                                <div style={{ marginRight: 'var(--abyss-space-sm)' }}> {value}</div>
                              </Indicator>
                            </div>
                          ) : (
                            <React.Fragment>{value}</React.Fragment>
                          )}
                        </div>
                      </Layout.Group>
                    </Accordion.Header>
                  </Accordion.Trigger>
                  <Accordion.Content>
                    <Table rows={sources} type={type} />
                  </Accordion.Content>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Widget>
    </ErrorHandler>
  );
}
